import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import moment from 'moment/moment';

import LoaderComponent from '@components/LoaderComponent';
import { toastError, toastSuccess, toastWarning } from '@components/Toast';
import BottomActionWrapper from '@components/BottomActionWrapper';

import AssignSupplierAndHandoverDate from '@features/order/components/AssignSupplierAndHandoverDate';
import StyleInfo from '@features/order/components/StyleInfo';
import FactoryPriceApprovalRequestModal from '@features/order/components/FactoryPriceApprovalRequestModal';
import { SUPPLIER_SELECT_VIEW } from '@features/favourites/utils/favouriteUtil';
import Margin from '@features/order/components/Margin';
import EtdDateFilter from '@features/order-dashboard/component/EtdDateFilter';

import { fetchOrderPlaceWorkflow } from '@store/orders/actions';
import { getOrderDetailsInfo, getSupplier } from '@store/reducers';
import { fetchSupplier } from '@store/supplier/action';
import { closeLoader, openLoader } from '@store/actions/LoaderAction';

import { updateSupplierBulkQuote } from '@services/commonService';
import { tagSupplierQuote, updateDeliveryDate } from '@services/quoteServices';

import ApiClient from '@lib/apiClient';

const initialSupplierAndETD = {
	factoryHandoverDate: '',
	factoryDeliveryDate: '',
	supplierId: null,
	searchSupplier: '',
	supplierName: null,
	quantityIncrease: ''
};

const Supplier = ({ orderId }) => {
	const [orderStyles, setOrderStyles] = useState([]);
	const [listOfOrderStyles, setListOfOrderStyles] = useState([]);
	const [selectedDesigns, setSelectedDesigns] = useState([]);
	const [page] = useState(0);
	const [sort] = useState('id,desc');
	const [isLoading, setIsLoading] = useState(false);
	const [isSelectAll, setIsSelectAll] = useState(false);
	const [errors, setErrors] = useState([]);
	const [isAssignSupplier, setIsAssignSupplier] = useState(false);
	const [supplierAndETDValue, setSupplierAndETDValue] = useState(initialSupplierAndETD);
	const [showSupplierList, setShowSupplierList] = useState(false);
	let orderDetailsInfo = useSelector(getOrderDetailsInfo);
	const supplierInfo = useSelector(getSupplier);
	const [selectView, setSelectView] = useState(SUPPLIER_SELECT_VIEW.QTY_PRICE.key);
	const [selectedDate, setSelectedDate] = useState('All');
	const [minDate, setMinDate] = useState(null);
	const [maxDate, setMaxDate] = useState(null);
	const [updateType, setUpdateType] = useState(null);
	const [showFactoryPriceApprovalModal, setShowFactoryPriceApprovalModal] = useState(false);

	const dispatch = useDispatch();
	const navigate = useNavigate();

	// The approver acts on the factory price from the margin view, so open it directly for that route.
	useEffect(() => {
		const params = new URLSearchParams(window.location.search);
		if (params.get('routeFrom') === 'FACTORY_PRICE_APPROVAL')
			setSelectView(SUPPLIER_SELECT_VIEW.MARGIN.key);
	}, []);

	const getUniqueDeliveryDates = (items) => {
		let etdDates = [];
		items.forEach((item) => {
			item.etdWiseInfoList.forEach((etd) => {
				etdDates.push(etd.deliveryDate);
			});
		});
		return [...new Set(etdDates)];
	};

	useEffect(() => {
		let tmpOrderStyles = listOfOrderStyles;
		if (selectedDate !== 'All') {
			let styles = [];
			listOfOrderStyles.forEach((item) => {
				let isExist = item.etdWiseInfoList.find((etd) => etd.deliveryDate === selectedDate);
				if (isExist) {
					styles.push(item);
				}
			});
			tmpOrderStyles = styles;
		}
		setOrderStyles(tmpOrderStyles);
	}, [listOfOrderStyles, selectedDate]);

	const uniqueDeliveryDates = getUniqueDeliveryDates(listOfOrderStyles);

	const handleDateSelection = (date) => {
		setSelectedDate(date);
	};

	const getTotal = (items) => {
		return items.reduce((total, pair) => {
			if (pair.factoryQuantity) {
				return total + parseInt(pair.factoryQuantity);
			}
			return total;
		}, 0);
	};

	let colorWiseQuantity = {};
	let sizeWiseQuantity = {};

	orderStyles.forEach((item) => {
		let colorLists = {};
		item?.colorWiseSizeQuantityPairList.forEach((value) => {
			colorLists[value.id] = getTotal(value.sizeQuantityPairList);
		});
		colorWiseQuantity[item.baseRfqId] = colorLists;
	});

	orderStyles.forEach((item) => {
		let sizeLists = {};
		// Colors do not have to carry the same sizes, so every size accumulates from zero. Seeding the map from
		// the first color alone left any size it lacks starting at undefined, which made the whole size NaN.
		item?.colorWiseSizeQuantityPairList.forEach((value) => {
			value.sizeQuantityPairList.forEach((style) => {
				sizeLists[style.id] = (sizeLists[style.id] || 0) + (style.factoryQuantity || 0);
			});
		});
		sizeWiseQuantity[item.baseRfqId] = sizeLists;
	});

	const renderColorWiseTotalPrice = (productId) => {
		let total = 0;
		let item = orderStyles.find((design) => parseInt(design.baseRfqId) === parseInt(productId));
		let quantityList = colorWiseQuantity[productId] || {};
		if (item) {
			for (let colorId in item.colorWiseFactoryPrice) {
				const price = parseFloat(item.colorWiseFactoryPrice[colorId]);
				const quantity = parseInt(quantityList[colorId]);
				// A price can outlive the color it was set for; counting it would turn the whole total into NaN.
				if (isNaN(price) || isNaN(quantity)) continue;
				total += price * quantity;
			}
		}
		return total.toFixed(2);
	};

	const renderSizeWiseTotalPrice = (productId) => {
		let total = 0;
		let item = orderStyles.find((design) => parseInt(design.baseRfqId) === parseInt(productId));
		let quantityList = sizeWiseQuantity[productId] || {};
		if (item) {
			for (let sizeCode in item.sizeWiseFactoryPrice) {
				const price = parseFloat(item.sizeWiseFactoryPrice[sizeCode]);
				const quantity = parseInt(quantityList[sizeCode]);
				// A price can outlive the size it was set for; counting it would turn the whole total into NaN.
				if (isNaN(price) || isNaN(quantity)) continue;
				total += price * quantity;
			}
		}
		return total.toFixed(2);
	};

	const renderDesignWiseTotalPrice = (productId) => {
		let total = 0;
		let item = orderStyles.find((design) => parseInt(design.baseRfqId) === parseInt(productId));
		if (item && item.totalFactoryQuantity) {
			total += item.totalFactoryQuantity * parseFloat(item.designWiseFactoryPrice);
		} else {
			total += item?.quantity * parseFloat(item?.designWiseFactoryPrice);
		}
		return total == null || isNaN(total) ? '0.00' : total.toFixed(2);
	};

	const onGetToalPrice = (id, designType) => {
		if (designType === 'COLORWISE') {
			return renderColorWiseTotalPrice(id);
		} else if (designType === 'SIZEWISE') {
			return renderSizeWiseTotalPrice(id);
		} else return renderDesignWiseTotalPrice(id);
	};

	const onRemoveSupplier = async (item) => {
		let rfqIdList = item?.etdWiseInfoList?.map((item) => item.rfqId);
		setIsLoading(true);
		await ApiClient.DELETE('unTagQuoteSupplier', {}, `?quoteIds=${rfqIdList.join(',')}`)
			.then(({ data }) => {
				setIsLoading(false);
				if (data && data.success) {
					getAllSuplierQuotes(orderId);
					dispatch(fetchOrderPlaceWorkflow(orderId));
					setSelectedDesigns([]);
					setIsSelectAll(false);
					toastSuccess(data.message);
				}
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Request was unsuccessful.');
				}
			});
	};

	const getAllSuplierQuotes = async (orderId) => {
		setIsLoading(true);
		await ApiClient.GET('getAllSuplierQuotes', +orderId)
			.then(({ data }) => {
				setIsLoading(false);
				setListOfOrderStyles(
					data.map((item) => {
						return {
							...item,
							designWiseFactoryPrice: item?.designWiseFactoryPrice || null,
							referenceNumber: item?.productReferenceNumber || '',
							factoryDeliveryDate: item.factoryDeliveryDate || item.deliveryDate,
							factoryHandoverDate: item?.factoryHandoverDate || item.orderStartDate,
							factoryQuotationType: item.factoryQuotationType || item.buyerQuotationType,
							factoryPriceType: item.factoryPriceType || item.priceType || 'FOB'
						};
					})
				);
			})
			.catch(({ response }) => {
				setIsLoading(false);
				if (response && response.data && response.data.message) {
					toastError(response.data.message);
				} else {
					toastError('Something went wrong! Please try again.');
				}
			});
	};
	const onSetOrderStyles = (data) => {
		setListOfOrderStyles(data);
	};

	useEffect(() => {
		getAllSuplierQuotes(orderId);
	}, [orderId]);

	useEffect(() => {
		dispatch(fetchSupplier(page, 20, supplierAndETDValue.searchSupplier, 'GARMENT', sort)).finally(() => {
			setIsLoading(false);
		});
	}, [supplierAndETDValue.searchSupplier]);

	const onSelectDesign = (id) => {
		const selectedIds = [...selectedDesigns];
		const findIdx = selectedIds.indexOf(id);
		if (findIdx > -1) {
			selectedIds.splice(findIdx, 1);
			setIsSelectAll(false);
		} else {
			selectedIds.push(id);
		}
		setSelectedDesigns([...selectedIds]);
	};

	const onSelectAll = (mark) => {
		if (mark) {
			let ids = orderStyles.filter((i) => !i?.factoryPriceApprovalLocked).map((i) => i.baseRfqId);
			setSelectedDesigns(ids);
		} else {
			setSelectedDesigns([]);
		}
	};
	const isDisable = () => {
		return orderStyles.length === 0;
	};

	const openInNewTab = () => {
		navigate(`/suppliers/add?orderId=${orderId}`);
	};
	const isValidDates = () => {
		let isValid = true;
		const newErrors = [...errors];
		if (!supplierAndETDValue?.supplierId && updateType === 'SUPPLIER') {
			!errors.includes('SUPPLIER_ERROR') && newErrors.push('SUPPLIER_ERROR');
			isValid = false;
		}
		if (!supplierAndETDValue?.factoryDeliveryDate && updateType === 'FACTORY_ETD') {
			!errors.includes('DELIVERY_ERROR') && newErrors.push('DELIVERY_ERROR');
			isValid = false;
		}
		if (!supplierAndETDValue?.factoryHandoverDate && updateType === 'FACTORY_ETD') {
			!errors.includes('HANDOVER_ERROR') && newErrors.push('HANDOVER_ERROR');
			isValid = false;
		}
		setErrors(newErrors);
		return isValid;
	};

	const findMaxDate = (dates) => {
		const dateObjects = dates.map((dateStr) => new Date(dateStr));
		const maxDate = new Date(Math.max(...dateObjects));
		return maxDate.toISOString().split('T')[0];
	};

	const handleAssignSupplierAndETD = (type) => {
		let selectedStyles = orderStyles?.filter((item) => selectedDesigns.includes(item.baseRfqId));
		let tmpList = [];
		selectedStyles.forEach((item) => {
			item?.etdWiseInfoList.forEach((etd) => {
				tmpList.push({
					deliveryDate: etd.deliveryDate,
					poReceiveDate: etd.poReceiveDate
				});
			});
		});
		let buyerEtds = tmpList.map((etd) => etd.deliveryDate);
		let poReceiveDates = tmpList.map((etd) => etd.poReceiveDate);
		let uniqueBuyerEtds = [...new Set(buyerEtds)];

		if (uniqueBuyerEtds.length > 1 && selectedDate === 'All') {
			toastWarning('Buyer ETD must be same!');
		} else {
			let selectedMinDate = moment(findMaxDate(poReceiveDates), 'YYYY-MM-DD');
			let selectedMaxDate = moment(uniqueBuyerEtds[0], 'YYYY-MM-DD');
			setMinDate(selectedMinDate);
			setMaxDate(selectedMaxDate);
			setIsAssignSupplier(true);
			setUpdateType(type);
		}
	};

	// handle close supplier handler *(close assign supplier and handover to factory ETD modal)
	const handleCloseAssignSupplierAndETD = () => {
		setIsAssignSupplier(false);
		setMinDate(null);
		setMaxDate(null);
		setSelectedDesigns([]);
		setIsSelectAll(false);
		setUpdateType(null);
	};

	// handle selected supplier id
	const handleSelectedSupplier = (id) => {
		const findSupplier = supplierInfo?.data.find((supplier) => supplier?.id === id);
		if (findSupplier) {
			setSupplierAndETDValue((prev) => ({
				...prev,
				supplierId: findSupplier?.id,
				supplierName: findSupplier?.name,
				searchSupplier: findSupplier?.name
			}));
			setShowSupplierList(false);
		}
		setErrors([]);
	};

	// handle Supplier and ETD value
	const handleSupplierAndETDChange = (e) => {
		const { name, value } = e.target;
		setSupplierAndETDValue((prev) => ({
			...prev,
			[name]: value
		}));
		if (value !== '') setErrors([]);
	};
	const onChangeDate = (fieldName, value) => {
		setSupplierAndETDValue((prev) => ({
			...prev,
			[fieldName]: value
		}));
	};
	const onUpdateStylePrice = () => {
		let selectedStyles = orderStyles?.filter((item) => selectedDesigns.includes(item.baseRfqId));
		let tmpList = [];
		selectedStyles.forEach((item) => {
			let rfqIdList = item?.etdWiseInfoList
				?.filter((etd) => {
					if (selectedDate === 'All' || selectedDate === etd.deliveryDate) {
						return etd;
					}
				})
				?.map((item) => item.rfqId);
			let tmpObj = {
				factoryQuotationType: item.buyerQuotationType,
				quoteIds: rfqIdList,
				deliveryDate: item.factoryDeliveryDate,
				priceType: item.priceType
			};
			if (item.buyerQuotationType === 'COLORWISE') {
				tmpObj['colorWisePrice'] = item.colorWiseBuyerPrice;
			} else if (item.buyerQuotationType === 'SIZEWISE') {
				tmpObj['sizeWisePrice'] = item.sizeWiseBuyerPrice;
			} else {
				tmpObj['designWisePrice'] = item.designWiseBuyerPrice;
			}
			tmpList.push(tmpObj);
		});
		return tmpList;
	};

	const getSelectedStyles = () => orderStyles.filter((style) => selectedDesigns.includes(style.baseRfqId));

	// A style stays locked until the approver acts or the price is edited, which supersedes the request.
	const getSendFactoryPriceApprovalBlockReason = () => {
		const selectedStyles = getSelectedStyles();

		if (selectedStyles.length === 0) return 'Select at least one style';

		if (selectedStyles.some((style) => !style?.supplierId))
			return 'Assign a supplier to the selected style first';

		// Only one request is open at a time, and a pending one already follows every price update.
		if (selectedStyles.some((style) => style?.factoryPriceApprovalStatus === 'PENDING'))
			return 'A selected style is already waiting for approval';

		if (selectedStyles.some((style) => style?.factoryPriceApprovalLocked))
			return 'Update the factory price of the selected style to send it for approval again';

		return '';
	};

	const canSendFactoryPriceForApproval = () => getSendFactoryPriceApprovalBlockReason() === '';

	const openFactoryPriceApprovalModal = () => {
		const blockReason = getSendFactoryPriceApprovalBlockReason();
		if (blockReason) {
			toastWarning(blockReason);
			return;
		}
		setShowFactoryPriceApprovalModal(true);
	};

	const handleSendFactoryPriceForApproval = async ({ remarks, attachments }) => {
		const selectedStyles = getSelectedStyles();
		const quoteIds = selectedStyles.flatMap((style) =>
			(style.etdWiseInfoList || []).map((info) => info.rfqId)
		);

		if (quoteIds.length === 0) {
			toastWarning('Select at least one style to send the factory price for approval');
			return;
		}

		const blockReason = getSendFactoryPriceApprovalBlockReason();
		if (blockReason) {
			toastWarning(blockReason);
			return;
		}

		setShowFactoryPriceApprovalModal(false);
		setIsLoading(true);
		try {
			const { data } = await ApiClient.POST('sendFactoryPriceApproval', {
				quoteIds,
				remarks,
				attachments
			});
			toastSuccess(data?.message || 'Factory price approval request sent successfully');
			setSelectedDesigns([]);
			setIsSelectAll(false);
			await getAllSuplierQuotes(orderId);
		} catch (error) {
			toastError(error?.response?.data?.message || 'Something went wrong! Please try again.');
		}
		setIsLoading(false);
	};

	// Removing the factory from one purchase order splits it into its own style so it can get another factory.
	const onRemoveFactoryFromPo = async (etd) => {
		if (!etd?.rfqId) return;
		setIsLoading(true);
		try {
			const { data } = await ApiClient.POST('splitStyleFromSupplier', { quoteIds: [etd.rfqId] });
			if (data?.success) {
				await getAllSuplierQuotes(orderId);
				dispatch(fetchOrderPlaceWorkflow(orderId));
				setSelectedDesigns([]);
				setIsSelectAll(false);
				toastSuccess(data.message);
			}
		} catch (error) {
			toastError(error?.response?.data?.message || 'Request was unsuccessful.');
		}
		setIsLoading(false);
	};

	// submit Supplier and ETD data
	const handleSubmitSupplierAndETD = async () => {
		if (!isValidDates()) return;
		if (orderDetailsInfo?.stream === 'STREAM_THREE') {
			let quoteList = onUpdateStylePrice();
			if (quoteList.length > 0) {
				try {
					dispatch(openLoader());
					await updateSupplierBulkQuote(quoteList);
					dispatch(closeLoader());
				} catch {
					dispatch(closeLoader());
				}
			}
		}
		const selectedStyles = orderStyles.filter((style) => selectedDesigns.includes(style.baseRfqId));

		setIsLoading(true);
		let data = null;
		if (updateType === 'FACTORY_ETD') {
			const body = {
				deliveryDate: supplierAndETDValue.factoryDeliveryDate,
				factoryHandoverDate: supplierAndETDValue.factoryHandoverDate,
				extraQuantityPercentage: supplierAndETDValue.quantityIncrease || null,
				quoteIds: selectedStyles.flatMap((style) =>
					style.etdWiseInfoList
						?.filter((etd) => {
							if (selectedDate === 'All' || selectedDate === etd.deliveryDate) {
								return etd;
							}
						})
						.map((info) => info.rfqId)
				)
			};
			data = await updateDeliveryDate(body);
		} else {
			const body = {
				quoteIds: selectedStyles.flatMap((style) => style.etdWiseInfoList.map((info) => info.rfqId))
			};
			data = await tagSupplierQuote(supplierAndETDValue.supplierId, body);
		}
		if (data && data.success) {
			setIsAssignSupplier(false);
			handleCloseAssignSupplierAndETD();
			dispatch(fetchOrderPlaceWorkflow(orderId));
			setSupplierAndETDValue(initialSupplierAndETD);
			await getAllSuplierQuotes(orderId);
			toastSuccess(data.message);
		}
		setIsLoading(false);
	};

	return (
		<LoaderComponent loading={isLoading}>
			<div className="create-order-container">
				<div className="assign-supplier-tab">
					<div className="inner-tabs">
						<ul>
							<li
								className={selectView === SUPPLIER_SELECT_VIEW.QTY_PRICE.key ? 'active' : ''}
								onClick={async () => {
									await getAllSuplierQuotes(orderId);
									setSelectView(SUPPLIER_SELECT_VIEW.QTY_PRICE.key);
								}}
							>
								Qty & Price
							</li>
							<li
								className={selectView === SUPPLIER_SELECT_VIEW.MARGIN.key ? 'active' : ''}
								onClick={() => {
									setSelectView(SUPPLIER_SELECT_VIEW.MARGIN.key);
								}}
							>
								Margin
							</li>
						</ul>
					</div>
					{selectView === SUPPLIER_SELECT_VIEW.QTY_PRICE.key && (
						<>
							<div className="d-flex align-items-center align-self-center justify-content-between supplier-title my-3">
								<h3 className="regular-16 weight-600 gray_dark_01 m-0">
									Assign supplier and set production cost
								</h3>
								<EtdDateFilter
									selectedDate={selectedDate}
									handleDateSelection={handleDateSelection}
									uniqueDeliveryDates={uniqueDeliveryDates}
									dateFlag="B"
								/>
							</div>
							<div className="selected-items-details d-flex">
								<div className="all-checkbox bg-gray-light select-all-designs">
									<div className={`custom-chekbox ${isDisable() ? 'disabled' : ''}`}>
										<div className="form-group m-0">
											<input
												type="checkbox"
												id="allSelect"
												name="allCheckBox"
												onChange={() => {
													setIsSelectAll(!isSelectAll);
													onSelectAll(!isSelectAll);
												}}
												checked={isSelectAll}
												disabled={isDisable()}
											/>
											<label htmlFor="allSelect" className="m-0">
												<span className="align-middle">Select all</span>
											</label>
										</div>
									</div>
								</div>
							</div>
							<StyleInfo
								selectedDate={selectedDate}
								stylesData={orderStyles}
								onSetOrderStyles={onSetOrderStyles}
								onGetToalPrice={onGetToalPrice}
								onSelectDesign={onSelectDesign}
								selectedDesigns={selectedDesigns}
								onRemoveSupplier={onRemoveSupplier}
								onRemoveFactoryFromPo={onRemoveFactoryFromPo}
								colorWiseQuantity={colorWiseQuantity}
								sizeWiseQuantity={sizeWiseQuantity}
								getAllSuplierQuotes={getAllSuplierQuotes}
								orderId={orderId}
								isReadOnly={orderDetailsInfo?.stream === 'STREAM_THREE'}
								isMarketPlace={orderDetailsInfo?.stream === 'STREAM_THREE'}
							/>
						</>
					)}
					{selectView === SUPPLIER_SELECT_VIEW.MARGIN.key && <Margin />}
				</div>
				{isAssignSupplier && (
					<AssignSupplierAndHandoverDate
						updateType={updateType}
						onClose={handleCloseAssignSupplierAndETD}
						show={isAssignSupplier}
						onChange={handleSupplierAndETDChange}
						onSubmit={handleSubmitSupplierAndETD}
						suppliers={supplierInfo?.data}
						onSelectSupplier={handleSelectedSupplier}
						supplierAndETDValue={supplierAndETDValue}
						onNavigateToSupplierPage={openInNewTab}
						errors={errors}
						showSuppliers={showSupplierList}
						setShowSupplierList={setShowSupplierList}
						onChangeDate={onChangeDate}
						startDate={minDate}
						endDate={maxDate}
					/>
				)}
			</div>
			{selectedDesigns.length > 0 && (
				<BottomActionWrapper onClose={handleCloseAssignSupplierAndETD}>
					<button
						className="button text size36"
						onClick={() => handleAssignSupplierAndETD('SUPPLIER')}
					>
						Assign Supplier
					</button>
					<span className="regular-16 gray_dark_01 d-inline-block mx-2 selection-devider">|</span>
					<button
						className="button text size36"
						onClick={() => handleAssignSupplierAndETD('FACTORY_ETD')}
					>
						Assign ETD
					</button>
					<span className="regular-16 gray_dark_01 d-inline-block mx-2 selection-devider">|</span>
					<button
						className="button text size36"
						onClick={openFactoryPriceApprovalModal}
						disabled={!canSendFactoryPriceForApproval()}
						title={getSendFactoryPriceApprovalBlockReason()}
					>
						Send for Approval
					</button>
				</BottomActionWrapper>
			)}
			{showFactoryPriceApprovalModal && (
				<FactoryPriceApprovalRequestModal
					styleCount={getSelectedStyles().length}
					onClose={() => setShowFactoryPriceApprovalModal(false)}
					onSubmit={handleSendFactoryPriceForApproval}
				/>
			)}
		</LoaderComponent>
	);
};

export default Supplier;
